import { 
  collection, doc, getDoc, getDocs, query, orderBy, where, 
  addDoc, updateDoc, deleteDoc, setDoc, onSnapshot, limit, Timestamp, writeBatch, documentId
} from 'firebase/firestore';
import { db, auth } from './firebaseClient';
import logger from './loggerService';
import authService from './authService';

export type AgeGroup =
  | 'Early intervention (upto 6 years)'
  | '6 to 12 years'
  | '12 to 17 years'
  | '17 to 25 years'
  | '25 to 30 years'
  | 'Above 30 years';

export type CommunityRole = 'member' | 'admin' | 'owner';

export interface Community {
  id: string;
  name: string;
  description?: string;
  age_group: AgeGroup;
  created_at: string;
  slug?: string | null;
  tagline?: string | null;
  hero_statement?: string | null;
  focus_points?: string[] | null;
  age_band?: string | null;
  display_order?: number | null;
  whatsapp_group_link?: string | null;
}

export interface CommunityWithMeta extends Community {
  memberCount: number;
  isMember: boolean;
}

export interface Post {
  id: string;
  community_id: string;
  user_id: string;
  content: string;
  created_at: string;
  edited_at?: string | null;
  pinned: boolean;
}

export interface Comment {
  id: string;
  post_id: string;
  user_id: string;
  content: string;
  created_at: string;
  edited_at?: string | null;
}

export interface PostWithMeta extends Post {
  likeCount: number;
  commentCount: number;
  likedByMe: boolean;
}

export interface Profile {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  avatar_url?: string | null;
}

export interface CommunityMember {
  user_id: string;
  joined_at: string;
  role: CommunityRole;
  profile?: Profile | null;
}

export interface Resource {
  id: string;
  title: string;
  type: 'pdf' | 'video' | 'article';
  description?: string | null;
  url: string;
  thumbnail?: string | null;
  created_at: string;
}

interface MyMembershipRow {
  community_id: string;
  role: CommunityRole;
  joined_at: string;
}

async function getCurrentUserId() {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.uid;
}

const communityService = {
  logError(message: string, error: any) {
    logger.error(message, error, new Error(error.message));
  },

  async fetchMemberCounts(communityIds: string[]): Promise<Map<string, number>> {
    if (communityIds.length === 0) return new Map();
    const countMap = new Map<string, number>();
    
    // Process in chunks of 10 for 'in' queries
    const chunks: string[][] = [];
    for (let i = 0; i < communityIds.length; i += 10) {
      chunks.push(communityIds.slice(i, i + 10));
    }
    
    for (const chunk of chunks) {
      const q = query(collection(db, 'user_communities'), where('community_id', 'in', chunk));
      const snap = await getDocs(q);
      snap.docs.forEach(doc => {
        const data = doc.data();
        const count = countMap.get(data.community_id) || 0;
        countMap.set(data.community_id, count + 1);
      });
    }
    return countMap;
  },

  async fetchMyMemberships(): Promise<MyMembershipRow[]> {
    try {
      const userId = await getCurrentUserId();
      const q = query(collection(db, 'user_communities'), where('user_id', '==', userId));
      const snap = await getDocs(q);
      return snap.docs.map(doc => doc.data() as MyMembershipRow);
    } catch {
      return [];
    }
  },

  async listCommunities(): Promise<Community[]> {
    const q = query(collection(db, 'communities'), orderBy('display_order', 'asc'), orderBy('created_at', 'asc'));
    const snap = await getDocs(q);
    return snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Community));
  },

  async listCommunitiesWithMeta(): Promise<CommunityWithMeta[]> {
    const communities = await this.listCommunities();
    if (!communities.length) return [];

    let userId: string | null = null;
    try { userId = await getCurrentUserId(); } catch {}

    const ids = communities.map(c => c.id);
    const [memberCountMap, myMemberships] = await Promise.all([
      this.fetchMemberCounts(ids),
      userId ? this.fetchMyMemberships().catch(() => []) : Promise.resolve([])
    ]);

    const mySet = new Set<string>(myMemberships.map(m => m.community_id));

    return communities.map(c => ({
      ...c,
      memberCount: memberCountMap.get(c.id) || 0,
      isMember: userId ? mySet.has(c.id) : false,
    }));
  },

  async getCommunity(idOrSlug: string): Promise<Community> {
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(idOrSlug);
    if (isUUID) {
      const docSnap = await getDoc(doc(db, 'communities', idOrSlug));
      if (docSnap.exists()) return { id: docSnap.id, ...docSnap.data() } as Community;
      throw new Error('Community not found');
    } else {
      const q = query(collection(db, 'communities'), where('slug', '==', idOrSlug), limit(1));
      const snap = await getDocs(q);
      if (!snap.empty) return { id: snap.docs[0].id, ...snap.docs[0].data() } as Community;
      throw new Error('Community not found');
    }
  },

  async getCommunityMeta(idOrSlug: string): Promise<{ memberCount: number; isMember: boolean }> {
    const community = await this.getCommunity(idOrSlug);
    const communityId = community.id;
    
    await getCurrentUserId();
    const [memberCountMap, myMemberships] = await Promise.all([
      this.fetchMemberCounts([communityId]),
      this.fetchMyMemberships().catch(() => [])
    ]);

    const isMember = myMemberships.some(membership => membership.community_id === communityId);
    return { memberCount: memberCountMap.get(communityId) || 0, isMember };
  },

  async listMyCommunities(): Promise<Community[]> {
    const myMemberships = await this.fetchMyMemberships();
    if (!myMemberships.length) return [];

    const ids = myMemberships.map(m => m.community_id);
    const communities: Community[] = [];
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += 10) {
      chunks.push(ids.slice(i, i + 10));
    }
    
    for (const chunk of chunks) {
      const q = query(collection(db, 'communities'), where(documentId(), 'in', chunk));
      const snap = await getDocs(q);
      communities.push(...snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Community)));
    }
    return communities;
  },

  async joinCommunity(idOrSlug: string) {
    try {
      const community = await this.getCommunity(idOrSlug);
      const communityId = community.id;
      const userId = await getCurrentUserId();
      
      const docId = `${userId}_${communityId}`;
      const docRef = doc(db, 'user_communities', docId);
      const snap = await getDoc(docRef);
      if (snap.exists()) return;

      await setDoc(docRef, {
        user_id: userId,
        community_id: communityId,
        role: 'member',
        created_at: new Date().toISOString()
      });
    } catch (err) {
      logger.error('Failed to join community', { idOrSlug }, err as Error);
      throw err;
    }
  },

  async leaveCommunity(idOrSlug: string) {
    const community = await this.getCommunity(idOrSlug);
    const communityId = community.id;
    const userId = await getCurrentUserId();
    const docId = `${userId}_${communityId}`;
    await deleteDoc(doc(db, 'user_communities', docId));
  },

  async listMembers(idOrSlug: string): Promise<CommunityMember[]> {
    const community = await this.getCommunity(idOrSlug);
    const communityId = community.id;
    
    const q = query(collection(db, 'user_communities'), where('community_id', '==', communityId), orderBy('created_at', 'asc'));
    const snap = await getDocs(q);
    
    const members = snap.docs.map(doc => doc.data() as any);
    const userIds = members.map(m => m.user_id);
    
    const profiles = new Map<string, Profile>();
    if (userIds.length > 0) {
      const chunks: string[][] = [];
      for (let i = 0; i < userIds.length; i += 10) {
        chunks.push(userIds.slice(i, i + 10));
      }
      for (const chunk of chunks) {
        const pQ = query(collection(db, 'profiles'), where('__name__', 'in', chunk));
        const pSnap = await getDocs(pQ);
        pSnap.docs.forEach(doc => {
          profiles.set(doc.id, { id: doc.id, ...doc.data() } as Profile);
        });
      }
    }

    return members.map(m => ({
      user_id: m.user_id,
      joined_at: m.created_at,
      role: m.role as CommunityRole,
      profile: profiles.get(m.user_id) || null
    }));
  },

  async updateCommunityProfile(payload: {
    communityId: string;
    name: string;
    tagline?: string | null;
    description?: string | null;
    focusPoints?: string[] | null;
    ageBand?: string | null;
    heroStatement?: string | null;
  }): Promise<Community> {
    const docRef = doc(db, 'communities', payload.communityId);
    
    const updateData: any = {
      name: payload.name,
      tagline: payload.tagline ?? null,
      description: payload.description ?? null,
      focus_points: payload.focusPoints ?? null,
      age_band: payload.ageBand ?? null,
      hero_statement: payload.heroStatement ?? null
    };

    await updateDoc(docRef, updateData);
    const updated = await getDoc(docRef);
    return { id: updated.id, ...updated.data() } as Community;
  },

  async grantAdminAccess(communityId: string, email: string): Promise<void> {
    const pQ = query(collection(db, 'profiles'), where('email', '==', email), limit(1));
    const pSnap = await getDocs(pQ);
    if (pSnap.empty) throw new Error('User not found with this email');
    
    const targetUserId = pSnap.docs[0].id;
    const docId = `${targetUserId}_${communityId}`;
    const docRef = doc(db, 'user_communities', docId);
    
    const uSnap = await getDoc(docRef);
    if (uSnap.exists()) {
      await updateDoc(docRef, { role: 'admin' });
    } else {
      await setDoc(docRef, {
        user_id: targetUserId,
        community_id: communityId,
        role: 'admin',
        created_at: new Date().toISOString()
      });
    }
  },

  async revokeAdminAccess(communityId: string, userId: string): Promise<void> {
    const docId = `${userId}_${communityId}`;
    await updateDoc(doc(db, 'user_communities', docId), { role: 'member' });
  },

  // Posts
  async listPosts(idOrSlug: string): Promise<PostWithMeta[]> {
    const community = await this.getCommunity(idOrSlug);
    const communityId = community.id;
    const userId = await getCurrentUserId();
    
    const q = query(
      collection(db, 'community_posts'), 
      where('community_id', '==', communityId)
    );
    const snap = await getDocs(q);
    const rawPosts = snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Post));
    
    // Sort logic manually due to Firestore composite index limitations
    const sortedPosts = rawPosts.sort((a, b) => {
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

    const postIds = sortedPosts.map(p => p.id);
    if (postIds.length === 0) return [];

    const likeMap = new Map<string, { count: number; likedByMe: boolean }>();
    const commentMap = new Map<string, number>();

    const chunks: string[][] = [];
    for (let i = 0; i < postIds.length; i += 10) {
      chunks.push(postIds.slice(i, i + 10));
    }
    
    for (const chunk of chunks) {
      const lQ = query(collection(db, 'post_likes'), where('post_id', 'in', chunk));
      const lSnap = await getDocs(lQ);
      lSnap.docs.forEach(doc => {
        const data = doc.data();
        const entry = likeMap.get(data.post_id) || { count: 0, likedByMe: false };
        entry.count += 1;
        if (data.user_id === userId) entry.likedByMe = true;
        likeMap.set(data.post_id, entry);
      });

      const cQ = query(collection(db, 'post_comments'), where('post_id', 'in', chunk));
      const cSnap = await getDocs(cQ);
      cSnap.docs.forEach(doc => {
        const data = doc.data();
        commentMap.set(data.post_id, (commentMap.get(data.post_id) || 0) + 1);
      });
    }

    return sortedPosts.map(p => ({
      ...p,
      likeCount: likeMap.get(p.id)?.count || 0,
      likedByMe: likeMap.get(p.id)?.likedByMe || false,
      commentCount: commentMap.get(p.id) || 0,
    }));
  },

  async createPost(idOrSlug: string, content: string): Promise<Post> {
    const community = await this.getCommunity(idOrSlug);
    const communityId = community.id;
    const userId = await getCurrentUserId();
    
    const newPost = {
      community_id: communityId,
      user_id: userId,
      content,
      created_at: new Date().toISOString(),
      pinned: false
    };
    
    const docRef = await addDoc(collection(db, 'community_posts'), newPost);
    return { id: docRef.id, ...newPost } as Post;
  },

  async deletePost(postId: string) {
    await deleteDoc(doc(db, 'community_posts', postId));
  },

  async pinPost(postId: string, pinned: boolean) {
    await updateDoc(doc(db, 'community_posts', postId), { pinned });
  },

  // Likes
  async likePost(postId: string) {
    const userId = await getCurrentUserId();
    const docId = `${userId}_${postId}`;
    await setDoc(doc(db, 'post_likes', docId), { post_id: postId, user_id: userId });
  },

  async unlikePost(postId: string) {
    const userId = await getCurrentUserId();
    const docId = `${userId}_${postId}`;
    await deleteDoc(doc(db, 'post_likes', docId));
  },

  // Comments
  async listComments(postId: string): Promise<Comment[]> {
    const q = query(collection(db, 'post_comments'), where('post_id', '==', postId), orderBy('created_at', 'asc'));
    const snap = await getDocs(q);
    return snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Comment));
  },

  async addComment(postId: string, content: string): Promise<Comment> {
    const userId = await getCurrentUserId();
    const newComment = {
      post_id: postId,
      user_id: userId,
      content,
      created_at: new Date().toISOString()
    };
    const docRef = await addDoc(collection(db, 'post_comments'), newComment);
    return { id: docRef.id, ...newComment } as Comment;
  },

  // Resources
  async listResourcesForCommunity(idOrSlug: string): Promise<Resource[]> {
    const community = await this.getCommunity(idOrSlug);
    const communityId = community.id;
    
    const q = query(collection(db, 'community_resources'), where('community_id', '==', communityId));
    const snap = await getDocs(q);
    const resourceIds = snap.docs.map(doc => doc.data().resource_id);
    
    if (resourceIds.length === 0) return [];
    
    const resources: Resource[] = [];
    const chunks: string[][] = [];
    for (let i = 0; i < resourceIds.length; i += 10) {
      chunks.push(resourceIds.slice(i, i + 10));
    }
    
    for (const chunk of chunks) {
      const rQ = query(collection(db, 'resources'), where(documentId(), 'in', chunk));
      const rSnap = await getDocs(rQ);
      resources.push(...rSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Resource)));
    }
    return resources;
  },

  async listAllResources(): Promise<Resource[]> {
    const q = query(collection(db, 'resources'), orderBy('created_at', 'desc'));
    const snap = await getDocs(q);
    return snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Resource));
  },

  async isAdmin(): Promise<boolean> {
    try {
      const localUser = authService.getCurrentUser();
      if (localUser?.email === 'badolahemal@gmail.com' || localUser?.email === 'nupurdube83@gmail.com') return true;

      const user = auth.currentUser;
      if (user?.email === 'badolahemal@gmail.com' || user?.email === 'nupurdube83@gmail.com') return true;
      
      const userId = localUser?.id || (await getCurrentUserId());
      const docSnap = await getDoc(doc(db, 'profiles', userId));
      return docSnap.exists() && docSnap.data().role === 'admin';
    } catch {
      return false;
    }
  },

  async adminCreateCommunity(payload: {
    name: string;
    slug: string;
    tagline?: string | null;
    description?: string | null;
    heroStatement?: string | null;
    focusPoints?: string[] | null;
    ageGroup: AgeGroup;
    ageBand?: string | null;
    displayOrder?: number | null;
  }): Promise<Community> {
    if (!(await this.isAdmin())) throw new Error('Admin access required to create communities');
    
    const newCommunity = {
      name: payload.name,
      slug: payload.slug,
      tagline: payload.tagline ?? null,
      description: payload.description ?? null,
      hero_statement: payload.heroStatement ?? null,
      focus_points: payload.focusPoints ?? null,
      age_group: payload.ageGroup,
      age_band: payload.ageBand ?? null,
      display_order: payload.displayOrder ?? null,
      created_at: new Date().toISOString()
    };
    
    const docRef = await addDoc(collection(db, 'communities'), newCommunity);
    return { id: docRef.id, ...newCommunity } as Community;
  },

  async adminUpdateCommunity(
    communityId: string,
    payload: {
      name?: string;
      slug?: string;
      tagline?: string | null;
      description?: string | null;
      heroStatement?: string | null;
      focusPoints?: string[] | null;
      ageBand?: string | null;
      displayOrder?: number | null;
    }
  ): Promise<Community> {
    if (!(await this.isAdmin())) throw new Error('Admin access required to update communities');

    const updateData: any = {};
    if (payload.name !== undefined) updateData.name = payload.name;
    if (payload.slug !== undefined) updateData.slug = payload.slug;
    if (payload.tagline !== undefined) updateData.tagline = payload.tagline;
    if (payload.description !== undefined) updateData.description = payload.description;
    if (payload.heroStatement !== undefined) updateData.hero_statement = payload.heroStatement;
    if (payload.focusPoints !== undefined) updateData.focus_points = payload.focusPoints;
    if (payload.ageBand !== undefined) updateData.age_band = payload.ageBand;
    if (payload.displayOrder !== undefined) updateData.display_order = payload.displayOrder;

    const docRef = doc(db, 'communities', communityId);
    await updateDoc(docRef, updateData);
    const updated = await getDoc(docRef);
    return { id: updated.id, ...updated.data() } as Community;
  },

  async adminDeleteCommunity(communityId: string): Promise<void> {
    if (!(await this.isAdmin())) throw new Error('Admin access required to delete communities');
    await deleteDoc(doc(db, 'communities', communityId));
  },

  async adminCreateResource(payload: {
    title: string;
    type: 'pdf' | 'video' | 'article';
    description?: string | null;
    url: string;
    thumbnail?: string | null;
  }): Promise<Resource> {
    if (!(await this.isAdmin())) throw new Error('Admin access required to create resources');

    const newResource = {
      title: payload.title,
      type: payload.type,
      description: payload.description ?? null,
      url: payload.url,
      thumbnail: payload.thumbnail ?? null,
      created_at: new Date().toISOString()
    };

    const docRef = await addDoc(collection(db, 'resources'), newResource);
    return { id: docRef.id, ...newResource } as Resource;
  },

  async adminLinkResourceToCommunity(communityId: string, resourceId: string): Promise<void> {
    if (!(await this.isAdmin())) throw new Error('Admin access required to link resources');
    const docId = `${communityId}_${resourceId}`;
    await setDoc(doc(db, 'community_resources', docId), { community_id: communityId, resource_id: resourceId });
  },

  async adminUnlinkResourceFromCommunity(communityId: string, resourceId: string): Promise<void> {
    if (!(await this.isAdmin())) throw new Error('Admin access required to unlink resources');
    const docId = `${communityId}_${resourceId}`;
    await deleteDoc(doc(db, 'community_resources', docId));
  },

  async adminDeleteResource(resourceId: string): Promise<void> {
    if (!(await this.isAdmin())) throw new Error('Admin access required to delete resources');
    await deleteDoc(doc(db, 'resources', resourceId));
  },

  async adminRemoveMember(communityId: string, userId: string): Promise<void> {
    if (!(await this.isAdmin())) throw new Error('Admin access required to remove members');
    const docId = `${userId}_${communityId}`;
    await deleteDoc(doc(db, 'user_communities', docId));
  },

  async adminGetCommunityStats(communityId: string): Promise<{
    memberCount: number;
    postCount: number;
    commentCount: number;
    activeMembers: number;
  }> {
    if (!(await this.isAdmin())) throw new Error('Admin access required to get community stats');

    const membersQ = query(collection(db, 'user_communities'), where('community_id', '==', communityId));
    const membersSnap = await getDocs(membersQ);
    
    const postsQ = query(collection(db, 'community_posts'), where('community_id', '==', communityId));
    const postsSnap = await getDocs(postsQ);
    
    const postIds = postsSnap.docs.map(doc => doc.id);
    let commentCount = 0;
    
    const chunks: string[][] = [];
    for (let i = 0; i < postIds.length; i += 10) {
      chunks.push(postIds.slice(i, i + 10));
    }
    
    for (const chunk of chunks) {
      const cQ = query(collection(db, 'post_comments'), where('post_id', 'in', chunk));
      const cSnap = await getDocs(cQ);
      commentCount += cSnap.size;
    }

    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
    const activePostsQ = query(
      collection(db, 'community_posts'), 
      where('community_id', '==', communityId),
      where('created_at', '>=', thirtyDaysAgo)
    );
    const activePostsSnap = await getDocs(activePostsQ);
    const uniqueActiveUsers = new Set(activePostsSnap.docs.map(doc => doc.data().user_id));

    return {
      memberCount: membersSnap.size,
      postCount: postsSnap.size,
      commentCount,
      activeMembers: uniqueActiveUsers.size
    };
  }
};

export default communityService;
